import { FC, useCallback, useMemo } from "react";
import { Checkbox } from "@App/Components/common/Checkbox";
import { ID } from "@App/types";

type FilterListProps = {
  availableItems: Item[];
  selectedItems: ID[];
  onChange: (items: ID[]) => void;
};

export interface Item {
  id: ID;
  name?: string;
  disabled?: boolean;
}

export const FilterList: FC<FilterListProps> = ({ availableItems, selectedItems, onChange }) => {
  const selectedSet = useMemo(() => new Set(selectedItems), [selectedItems]);

  const handleChange = useCallback(
    (itemId: ID, nextChecked: boolean) => {
      const item = availableItems.find((i) => i.id === itemId);
      if (!item || item.disabled) return; // ignore changes to disabled or non-existent items

      const isSelected = selectedSet.has(itemId);
      if (nextChecked === isSelected) return; // no state change needed

      const newSelected = nextChecked
        ? [...selectedItems, itemId]
        : selectedItems.filter((i) => i !== itemId);

      onChange(newSelected);
    },
    [onChange, selectedItems, selectedSet],
  );

  return (
    <>
      {availableItems.map((item) => (
        <Checkbox
          key={item.id}
          status={selectedSet.has(item.id)}
          title={item.name ?? String(item.id)}
          disabled={item.disabled}
          onChange={(checked) => !item.disabled && handleChange(item.id, checked)}
        />
      ))}
    </>
  );
};
